import { CSSProperties, useMemo, useState, Fragment } from "react";
import classes from "./Description.module.css";

import {
  ArrowSquareLeftOutlineIcon,
  ArrowSquareRightOutlineIcon,
  PlayButtonFill,
  QuestionIcon,
  ReviewIcon,
} from "@/components/Icons";
import ProductCard from "@/components/ProductCard";
import SectionHeader from "@/components/SectionHeader";

import Loader from "@/components/Loader";
import ProductCardV2 from "@/components/ProductCard/ProductCardV2";
import useFetch from "@/hooks/useFetch";
import {
  getProductLongDescription,
  getProductQuestions,
  getProductReview,
  getProductsByFilter,
  getRelativeProducts,
  getSimilarProductBySlug,
} from "@/requests/product";
import {
  convertFontSize,
  getTokenCookie,
  removeZone,
  toLoginPage,
} from "@/utils";

import { Navigation} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import { getS3ImageUrl } from "@/utils/routes";
import { placeholderImage } from "@/utils/constants";
import { RatingStarsOnlyStars } from "@/components/RatingStars";
import PopupManager from "@/components/PopupManager";
import { setGlobalPopupElement } from "@/store/features/globalSlice";
import { useAppDispatch } from "@/store/hooks";
import AskQuestionForm from "@/components/AskQuestionForm";
import { useRouter } from "@/hooks/useCompatRouter";
import ReviewForm from "@/components/order/ProductReviewForm";
import RelatedProductCard from "@/components/ProductCard/RelatedProductCard";
import SimilarProductCard from "@/components/ProductCard/SimilarProductCard";

interface Props {
  className?: string;
  style?: CSSProperties;

  product: ProductDetails;
  relatedProducts?: ProductItem[] | null;
}

const specId = "product-specification-id-123";
const descId = "product-description-id-123";
const reviewId = "product-review-id-123";
const questionId = "product-question-id-123";

const Description = ({
  className,
  style,

  product,
  relatedProducts,
}: Props) => {
  const dispatch = useAppDispatch();
  const token = getTokenCookie();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const descRes = useFetch(() => getProductLongDescription(product.slug), {});
  const productRes = useFetch(
    () => getRelativeProducts(product.slug || "", 4),
    {}
  );

  const reviewRes = useFetch<ProductReviewResponse>(
    () => getProductReview(product.slug, currentPage, 5),
    {
      deps: [currentPage as any],
    }
  );

  const questionRes = useFetch<QuestionListResponse>(
    () => getProductQuestions(product.id.toString(), 1, 20),
    {
      // deps: [currentPage as any],
    }
  );

  const skus = useMemo(() => {
    const _skus = [...product.skus];
    return _skus.sort((a, b) => a.price - b.price);
  }, [product.skus]);

  const similarProducts = useFetch(
    () =>
      getProductsByFilter({
        currentPage: 1,
        perPage: 5,
        query: {
          category: product.categories?.length
            ? product.categories[0].slug
            : product.category?.slug,
          price: `${Math.max(product.skus?.[0].discountedPrice - 10_000, 0)},${
            product.skus?.[0].price + 10_000
          }`,
        },
        withoutPrefix: true,
      }),
    {}
  );

  // console.log({ similarProducts });
  // console.log({ relatedProducts });

  // const similarProducts = useFetch(
  //   () => getSimilarProductBySlug(product.slug),
  //   {}
  // );

  const [activeTab, setActiveTab] = useState(0);

  const tabs: string[] = [
    // "Description",
    // "Specification",
    // "Reviews",
    // "Return & Refund Policy",
  ];

  const tabIds: string[] = [];

  if (product?.specifications?.length) {
    tabs.push("Specification");
    tabIds.push(specId);
  }

  if (!!descRes.response && descRes.response !== "null") {
    tabs.push("Description");
    tabIds.push(descId);
  }

  if (reviewRes.response && reviewRes.response !== null) {
    tabs.push(
      `Reviews ${
        reviewRes.response?.data && reviewRes.response?.data?.length > 0
          ? `(${reviewRes.response?.data?.length})`
          : "(0)"
      }`
    );

    tabIds.push(reviewId);
  }

  if (reviewRes.response && reviewRes.response !== null) {
    tabs.push(
      `Questions ${
        questionRes.response?.data && questionRes.response?.data?.length > 0
          ? `(${questionRes.response?.data?.length})`
          : "(0)"
      }`
    );
    tabIds.push(questionId);
  }

  function makeYouTubeVideosResponsive(html: string): string {
    if (!html) return "";

    // Regular expression to find iframe tags with YouTube
    const iframeRegex = /<iframe[^>]*(youtube|youtu\.be)[^>]*><\/iframe>/g;

    // Replace each YouTube iframe with a wrapped version
    return html.replace(iframeRegex, (match) => {
      return `<div class="responsive-video-container">${match}</div>`;
    });
  }

  const longDesc = useMemo(() => {
    return (
      makeYouTubeVideosResponsive(convertFontSize(descRes.response || "")) || ""
    );
  }, [descRes.response]);

  const classNames = [classes.Description, className];

  if (product.displayOnly)
    return (
      <div className={classNames.join(" ")} style={style}>
        <div className={`${classes.container} container`}>
          <>
            <div
              className="html-content overflow-auto hide-scrollbar"
              dangerouslySetInnerHTML={{
                __html: product.shortDescription,
              }}
            ></div>
            {descRes.loading && (
              <div className="flex justify-center py-4">
                <Loader size={60} />
              </div>
            )}
            <div
              className="html-content overflow-auto hide-scrollbar"
              dangerouslySetInnerHTML={{
                __html: longDesc,
              }}
            ></div>
          </>
        </div>
      </div>
    );

  const handleVideoPlay = (src: string) => {
    dispatch(
      setGlobalPopupElement(
        <PopupManager>
          <div className="bg-white w-[90vw] md:w-[500px] lg:w-[500px] rounded-md p-4 min-h-[200px]">
            <video
              className="w-[90vw] md:w-[500px] lg:w-[500px] rounded-md min-h-[200px]"
              controls
              autoPlay
            >
              <source src={getS3ImageUrl(src)} />
            </video>
          </div>
        </PopupManager>
      )
    );
  };

  const handleAddQuestion = () => {
    if (!token) {
      toLoginPage(router);
    } else
      dispatch(
        setGlobalPopupElement(
          <PopupManager>
            <AskQuestionForm product={product} />
          </PopupManager>
        )
      );
  };

  const handleCreateReview = () => {
    if (!token) {
      toLoginPage(router);
    } else {
      dispatch(
        setGlobalPopupElement(
          <PopupManager>
            <ReviewForm product={product} from="product" />
          </PopupManager>
        )
      );
    }
  };

  return (
    <div className={classNames.join(" ")} style={style}>
      <div className={`${classes.container} container`}>
        <div className={classes.body}>
          <div className={classes.left}>
            {/* <SectionHeader
                            title="Similar Products"
                            noContainer={true}
                            style={{
                                marginBottom: 14,
                                paddingBottom: 0,
                                paddingTop: 0,
                            }}
                        /> */}

            {/* <SimilarProductsRow slug={product.slug} /> */}

            <div className={classes.tabs + " hide-scrollbar"}>
              {tabs.map((tab, index) => {
                let classNames = `${classes.tab} ${
                  activeTab === index ? classes.active : ""
                }`;

                return (
                  <p
                    key={index + "-tab-" + tab.replace(/\s/g, "-").toLowerCase()}
                    className={classNames}
                    onClick={() => {
                      document.getElementById(tabIds[index])?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest",
                      });

                      const targetPosition =
                        document
                          .getElementById(tabIds[index])
                          ?.getBoundingClientRect().top || 0;

                      window.scrollBy({
                        top: targetPosition - 120,
                        behavior: "smooth",
                      });
                    }}
                  >
                    {tab}
                  </p>
                );
              })}
            </div>

            {!!product.specifications?.length && (
              <>
                <h3 className="text-xl font-semibold pb-2 text-primary">
                  Specification
                </h3>

                <Specification items={product.specifications} />
              </>
            )}

            {!!descRes.response && descRes.response !== "null" && (
              <div id={descId} className="pt-6">
                <h3 className="text-xl font-semibold pb-2 border-b border-gray-300 text-primary">
                  Description
                </h3>
                {/* <div
									className="html-content overflow-auto hide-scrollbar"
									dangerouslySetInnerHTML={{
										__html: product.shortDescription,
									}}></div> */}
                {descRes.loading && (
                  <div className="flex justify-center py-4">
                    <Loader size={60} />
                  </div>
                )}
                {descRes.response !== "null" && (
                  <div
                    className="html-content overflow-auto hide-scrollbar"
                    dangerouslySetInnerHTML={{
                      __html: longDesc,
                    }}
                  ></div>
                )}
              </div>
            )}

            <div className="mt-6">
              <h3 className="text-xl font-semibold pb-2 border-b border-gray-300 text-primary">
                What is the price of {product.shortName || product.name} in
                Bangladesh?
              </h3>
              <p className="mt-2">
                The latest price of {product.shortName || product.name} in
                Bangladesh is BDT {skus[0].discountedPrice || skus[0].price}.
                You can buy the {product.shortName || product.name} at best
                price from our website or visit any of our store .
              </p>
            </div>

            {reviewRes.response && (
              <div id={reviewId} className="pt-6">
                <div className="border-b border-gray-300 pb-2 flex justify-between">
                  <h3 className="text-xl font-semibold border-gray-300 text-primary">
                    Reviews
                  </h3>

                  <button
                    className="bg-primary/10 text-primary px-3 py-1 rounded"
                    onClick={handleCreateReview}
                  >
                    Write a Review
                  </button>
                </div>

                <div className="">
                  {reviewRes?.response?.data?.map((review, index) => {
                    return review.status === "active" ? (
                      <div key={index + "-review-" + (review.id || review.first_name + "-" + review.last_name)} className="flex items-start my-6 ">
                        <div className="basis-[10%] flex items-center justify-center">
                          <Image
                            src={
                              (review.avatar && getS3ImageUrl(review.avatar)) ||
                              placeholderImage
                            }
                            width={80}
                            height={80}
                            style={{
                              aspectRatio: 60 / 60,
                              objectFit: "cover",
                            }}
                            className="rounded-full border border-primary"
                            alt={`${review.first_name} ${review.last_name} avatar`}
                          />
                        </div>
                        <div className="basis-[90%]">
                          <div className="flex-col lg:flex-row items-end justify-between gap-2">
                            <div className="flex gap-2">
                              <p className="font-semibold -mb-1">
                                {review.first_name
                                  ? `${review.first_name} ${
                                      review.last_name ? review.last_name : ""
                                    }`
                                  : "Anonymous User"}
                              </p>
                              <RatingStarsOnlyStars
                                rating={review?.rating || 5}
                              />
                              <p>({review.rating})</p>
                            </div>

                            <p className="text-gray-500" suppressHydrationWarning>
                              on{" "}
                              {new Date(
                                removeZone(review.created_at || "")
                              ).toLocaleString()}
                            </p>
                          </div>

                          <div className="text-gray4 pt-2">{review.comment}</div>
                          {/* <div className="flex gap-2">
                            {review?.product_image
                              ?.split(",")
                              ?.map((img, index) => {
                                return (
                                  <figure
                                    key={index + "-product-image-" + (img.includes("mp4") ? "video" : "image")}
                                    className="border w-[120px] h-[100px] relative hover:cursor-pointer"
                                    onClick={
                                      img.includes("mp4")
                                        ? () => handleVideoPlay(img)
                                        : () => {}
                                    }
                                  >
                                    {img.includes("mp4") ? (
                                      <video
                                        className="w-[120px] h-[100px] object-cover"
                                        muted
                                      >
                                        <source src={getS3ImageUrl(img)} />
                                      </video>
                                    ) : (
                                      <Image
                                        src={
                                          (img && getS3ImageUrl(img)) ||
                                          placeholderImage
                                        }
                                        width={120}
                                        height={100}
                                        style={{
                                          aspectRatio: 1,
                                          objectFit: "cover",
                                        }}
                                        alt=""
                                      />
                                    )}

                                    {img.includes("mp4") && (
                                      <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
                                        <PlayButtonFill />
                                      </span>
                                    )}
                                  </figure>
                                );
                              })}
                          </div> */}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>

                {reviewRes.response?.data?.length === 0 && (
                  <div className="flex flex-col justify-center items-center my-8">
                    <ReviewIcon width={64} height={64} />
                    <p className="text-lg mt-6 mb-2">No Review Found</p>
                    <button
                      className="bg-primary/10 text-primary px-3 py-1 rounded"
                      onClick={handleCreateReview}
                    >
                      Write a Review
                    </button>
                  </div>
                )}
              </div>
            )}

            <div id={questionId} className="pt-6">
              <div className="border-b border-gray-300 pb-2 flex justify-between">
                <h3 className="text-xl font-semibold text-primary">
                  Questions
                </h3>

                <button
                  className="bg-primary/10 text-primary px-3 py-1 rounded"
                  onClick={handleAddQuestion}
                >
                  Ask a Question
                </button>
              </div>

              <div className="">
                {questionRes.response?.data.map((question, index) => {
                  return question?.status === "active" ? (
                    <div
                      key={index + "-question-" + (question.question_id || question.user_name)} 
                      className="my-3 space-y-2 border-b border-b-gray-300 pb-3"
                    >
                      <p>
                        <span className="text-lg font-medium">
                          {question?.user_name || "Anonymous User"}{" "}
                        </span>
                        <span className="text-sm text-gray-500" suppressHydrationWarning>
                          on{" "}
                          {new Date(
                            removeZone(question?.created_at || "")
                          ).toLocaleString()}
                        </span>
                      </p>

                      <div className="space-y-2 text-sm">
                        <div className="font-medium">
                          <span className="">Q: </span>
                          {question?.question_content}
                        </div>

                        <div>
                          <span className="font-medium">Ans: </span>
                          {question?.answer_content}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })}

                {questionRes.response?.data &&
                  questionRes.response?.data?.length === 0 && (
                    <div className="flex flex-col justify-center items-center my-8">
                      <QuestionIcon width={64} height={64} />
                      <p className="text-lg mt-6 mb-2">No Question Found</p>
                      <button
                        className="bg-primary/10 text-primary px-3 py-1 rounded"
                        onClick={handleAddQuestion}
                      >
                        Ask a Question
                      </button>
                    </div>
                  )}
              </div>
            </div>

            {/* {tabElements[activeTab]?.()} */}
          </div>

          <div className={classes.right}>
            {relatedProducts && (
              <div>
                <h2 className={classes.title}>Related Products</h2>
                <div className={classes.products}>
                  {relatedProducts.map((product:any, index) => {
                    // if (Number(product.stockQuantity) <= 0) return null;
                    return (
                      <div key={index + "-" + (product.slug || product.id) + "-related-product-card "} className="space-y-2">
                        {/* <ProductCard productItem={product} type="xs" /> */}
                        <RelatedProductCard productItem={product} type="xs" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {similarProducts.response?.data && (
              <div>
                <h3 className={classes.title}>Similar Products</h3>
                <div className={classes.products}>
                  {similarProducts?.response?.data.map((product:any, index) => {
                    if (Number(product.stockQuantity) <= 0) return null;
                    return (
                      <div key={index + "-" + (product.slug || product.p_id) + "-similar-product-card"} className="space-y-2">
                        {/* <ProductCardV2 productItem={product} type="xs" /> */}
                        <SimilarProductCard productItem={product} type="xs" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <div className={`container`}>
                <SectionHeader 
                    title='You May Like'
                    link='/products'
                    style={{
                        marginBottom: 0,
                        paddingBottom: 0
                    }}
                />
            </div> */}
      {/* <FeaturedProducts
                products={productItems.reverse()}
            /> */}
    </div>
  );
};

function groupByIsTitle(
  items: ProductDetailsSpecification[]
): { title: string; entries: ProductDetailsSpecification[] }[] {
  const grouped: { title: string; entries: ProductDetailsSpecification[] }[] =
    [];
  let currentGroup: {
    title: string;
    entries: ProductDetailsSpecification[];
  } | null = null;
  let noTitleGroup: {
    title: string;
    entries: ProductDetailsSpecification[];
  } | null = {
    title: "noTitle",
    entries: [],
  };

  items.forEach((item) => {
    if (item.isTitle) {
      if (currentGroup) {
        grouped.push(currentGroup);
      }
      currentGroup = { title: item.key, entries: [] };
    } else {
      if (currentGroup) {
        if (item.value) {
          currentGroup.entries.push({
            id: item.id,
            isTitle: item.isTitle,
            key: item.key,
            // keyId: item.id,
            value: item.value.split("\n").join("</br>"),
          });
        }
      } else {
        if (item.value) {
          noTitleGroup?.entries?.push({
            id: item.id,
            isTitle: item.isTitle,
            key: item.key,
            // keyId: item.id,
            value: item.value.split("\n").join("</br>"),
          });
        }
      }
    }
  });

  if (currentGroup) {
    grouped.push(currentGroup);
  }

  if (noTitleGroup.entries?.length > 0) {
    grouped.push(noTitleGroup);
  }

  return grouped;
}

const Specification = ({ items }: { items: ProductDetailsSpecification[] }) => {
  const specsMap = new Map<string, any[]>();
  const noTitle: any[] = [];
  let isExistTitle: boolean = false;

  items.forEach((item, index) => {
    if (item?.isTitle) {
      isExistTitle = true;

      const values = [];
      let i = index + 1;
      while (!items[i]?.isTitle && i < items?.length) {
        values.push(items[i]);
        i++;
      }
      specsMap.set(item.key, values);
    } else if (!isExistTitle) {
      noTitle.push(item);
    }
  });

  specsMap.set("noTitle", noTitle);

  const specs = groupByIsTitle(items);

  return (
    <table className={classes.specification}>
      <tbody>
        {specs.map((item, index) => {
          // if (item.entries.some((item) => !item.value)) return;
        if (item?.entries?.length <= 0) return null;

        return (
          <Fragment key={`group-${index}`}>
            {item.title !== "noTitle" && (
              <tr key={`title-${index}`}>
                <td
                  colSpan={2}
                  className="text-lg font-semibold bg-primary/10 rounded lg:rounded-lg"
                >
                  <div className="text-primary text-base  py-2 lg:py-0 px-2 lg:px-0">
                    {item.title}
                  </div>
                </td>
              </tr>
            )}

            {item.entries.map((spec, i) => {
              if (!spec.value) return null;

              return (
                <tr
                  key={`spec-${index}-${i}-${spec.id || spec.key}`}
                  className={`${
                    items[index + 1] && !items[index + 1]?.value
                      ? "border-b border-gray-200 py-3"
                      : "border-b border-gray-200 py-3"
                  } p-2`}
                >
                  {/* <td>{spec.key}</td>
                  <td
                    className="!font-normal"
                    dangerouslySetInnerHTML={{ __html: `${spec.value}` }}
                  ></td> */}
                  <td>
                    <div className="flex flex-col md:flex-row md:items-center">
                      <div className="basis-[30%]">{spec.key}</div>
                      <div
                        className="basis-[70%] !font-normal text-black text-base"
                        dangerouslySetInnerHTML={{ __html: `${spec.value}` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              );
            })}
            </Fragment>
          );

          // if (item.isTitle) {
          //   return (
          //     <tr key={index}>
          //       <td
          //         colSpan={2}
          //         className="text-lg font-semibold bg-primary/10 rounded lg:rounded-lg"
          //       >
          //         <p className="text-primary text-base  py-2 lg:py-0 px-2 lg:px-0">
          //           {item.key}
          //         </p>
          //       </td>
          //     </tr>
          //   );
          // } else {
          //   if (!item.value) return;

          //   return (
          //     <tr
          //       key={index}
          //       className={`${
          //         items[index + 1] && !items[index + 1]?.value
          //           ? ""
          //           : "border-b border-gray-200 py-3"
          //       } p-2`}
          //     >
          //       <td>{item.key}</td>
          //       <td
          //         className="!font-normal"
          //         dangerouslySetInnerHTML={{ __html: `${item.value}` }}
          //       ></td>
          //     </tr>
          //   );
          // }
        })}

        {items?.length === 0 && <td colSpan={2}>No specification found</td>}
      </tbody>
    </table>
  );
};

interface PropsSim {
  className?: string;
  style?: CSSProperties;

  colLimit?: number;

  slug?: string;
}

const SimilarProductsRow = ({
  className,
  style,

  colLimit = 4,

  slug,
}: PropsSim) => {
  const classNames = [classes.SimilarProductsRow, className];

  const productRes = useFetch(() => getRelativeProducts(slug || "", 4), {});

  const randomKey = Math.random().toString(36).substring(7);
  // const randomKey = useMemo(
  // 	() => Math.random().toString(36).substring(7),
  // 	[]
  // );

  if (productRes.loading || productRes.response?.length === 0) return null;

  return (
    <div className={classNames.join(" ")} style={style}>
      <SectionHeader
        title="Related Products"
        noContainer={true}
        style={{
          marginBottom: 14,
          paddingBottom: 0,
          paddingTop: 0,
        }}
      />

      <div className={classes.changer}>
        <button
          className={`${classes.button} ${classes.left}`}
          id={"swiper-button-prev-similar-product-" + randomKey}
        >
          <ArrowSquareLeftOutlineIcon className={classes.icon} />
        </button>

        <button
          className={`${classes.button} ${classes.right}`}
          id={"swiper-button-next-similar-product-" + randomKey}
        >
          <ArrowSquareRightOutlineIcon className={classes.icon} />
        </button>
      </div>

      <Swiper
        spaceBetween={12}
        navigation={{
          nextEl: "#swiper-button-next-similar-product-" + randomKey,
          prevEl: "#swiper-button-prev-similar-product-" + randomKey,
        }}
        breakpoints={{
          500: {
            slidesPerView: 2,
          },
          600: {
            slidesPerView: Math.min(colLimit - 1, 3),
          },
          980: {
            slidesPerView: colLimit,
          },
        }}
        slidesPerView={2}
        pagination={{
          clickable: true,
        }}
        className={classes.swiper}
        modules={[Navigation]}
      >
        {(productRes.response as any[])?.map((product, i) => (
          <SwiperSlide key={i + "-" + (product.slug || product.id) + "-similar-product-slide"}>
            <ProductCard productItem={product} key={i + "-" + (product.slug || product.id) + "-similar-product-card"} type="2lg" />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default Description;
